import { NavLink, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { ChevronDown } from "lucide-react";

const matchesPath = (item, pathname) =>
  Boolean(
    item.path &&
      (pathname === item.path || (!item.end && pathname.startsWith(`${item.path}/`))),
  );

const hasActiveDescendant = (item, pathname) =>
  item.children?.some(
    (child) => matchesPath(child, pathname) || hasActiveDescendant(child, pathname),
  ) || false;

function SidebarItem({ item, onNavigate, collapsed, isChild = false }) {
  const location = useLocation();
  const Icon = item.icon;

  const isChildActive = hasActiveDescendant(item, location.pathname);

  const [open, setOpen] = useState(isChildActive || false);

  useEffect(() => {
    if (isChildActive) setOpen(true);
  }, [isChildActive]);

  if (item.children) {
    return (
      <div className="relative group/nav">
        <button
          onClick={() => !collapsed && setOpen((o) => !o)}
          className={[
            "w-full flex items-center px-3 py-2.5 rounded-lg text-sm font-medium",
            "transition-all duration-200 cursor-pointer select-none",
            collapsed ? "justify-center" : "justify-between",
            isChildActive
              ? "bg-gradient-to-r from-violet-500 to-indigo-500 text-white shadow-md shadow-violet-900/25"
              : "text-slate-300 hover:bg-white/[0.07] hover:text-white",
          ].join(" ")}
        >
          <div className={`flex items-center min-w-0 ${collapsed ? "" : "gap-3"}`}>
            {Icon && (
              <Icon
                className={`w-[18px] h-[18px] flex-shrink-0 ${
                  isChildActive ? "text-white" : "text-slate-400 group-hover/nav:text-slate-200"
                }`}
              />
            )}
            {!collapsed && <span className="truncate">{item.name}</span>}
          </div>

          {!collapsed && (
            <ChevronDown
              className={`w-[15px] h-[15px] flex-shrink-0 opacity-60 transition-transform duration-200 ${
                open ? "rotate-0" : "-rotate-90"
              }`}
            />
          )}
        </button>

        {collapsed && (
          <div className="absolute left-full top-1/2 -translate-y-1/2 ml-3 px-2.5 py-1.5 bg-[#1e2447] text-white text-xs rounded-lg shadow-xl opacity-0 group-hover/nav:opacity-100 pointer-events-none whitespace-nowrap z-[999] border border-white/10 transition-opacity duration-150">
            {item.name}
          </div>
        )}

        {!collapsed && open && (
          <div className="mt-1 ml-4 pl-3.5 border-l border-white/[0.1] space-y-0.5 py-0.5">
            {item.children.map((child, i) => (
              <SidebarItem
                key={i}
                item={child}
                onNavigate={onNavigate}
                collapsed={false}
                isChild
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  const isActive = matchesPath(item, location.pathname);

  return (
    <div className="relative group/nav">
      <NavLink
        to={item.path}
        end={item.end}
        onClick={onNavigate}
        className={() =>
          [
            "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium",
            "transition-all duration-200 select-none",
            collapsed ? "justify-center" : "",
            isActive
              ? "bg-gradient-to-r from-violet-600 to-indigo-500 text-white shadow-md shadow-violet-900/25"
              : "text-slate-300 hover:bg-white/[0.07] hover:text-white",
          ].join(" ")
        }
      >
        {isChild && Icon ? (
          <Icon
            className={`w-4 h-4 flex-shrink-0 ${
              isActive ? "text-white" : "text-slate-500 group-hover/nav:text-violet-300"
            }`}
          />
        ) : isChild ? (
          <span
            className={`w-[7px] h-[7px] rounded-full flex-shrink-0 transition-colors ${
              isActive
                ? "bg-white"
                : "bg-slate-600 group-hover/nav:bg-violet-400"
            }`}
          />
        ) : Icon ? (
          <Icon
            className={`w-[18px] h-[18px] flex-shrink-0 ${
              isActive ? "text-white" : "text-slate-400 group-hover/nav:text-slate-200"
            }`}
          />
        ) : null}

        {!collapsed && <span className="truncate">{item.name}</span>}
      </NavLink>

      {collapsed && !isChild && (
        <div className="absolute left-full top-1/2 -translate-y-1/2 ml-3 px-2.5 py-1.5 bg-[#1e2447] text-white text-xs rounded-lg shadow-xl opacity-0 group-hover/nav:opacity-100 pointer-events-none whitespace-nowrap z-[999] border border-white/10 transition-opacity duration-150">
          {item.name}
        </div>
      )}
    </div>
  );
}

export default SidebarItem;
